import React from 'react';
import { ChevronLeft, Loader } from 'lucide-react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useTheme } from '../../../components/common/Theme';
import { useToast } from '../../../components/common/Toast';
import { ghostBtn, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import { FABRIC_HEADER_EYEBROW, LLM_GATEWAY_PATHS } from './llmGatewayConstants';
import { LlmGatewayProvider, useLlmGateway } from './llmGatewayStore';

export type LlmGatewayOutletContext = {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
};

function titleForPath(pathname: string): { title: string; subtitle: string } {
  if (pathname.endsWith('/new')) {
    return {
      title: 'Create LLM Gateway',
      subtitle: 'Publish a governed, protocol-compatible endpoint in front of a registered model.',
    };
  }
  if (pathname.endsWith('/edit')) {
    return {
      title: 'Edit LLM Gateway',
      subtitle: 'Update model binding, API types, authentication, and governance.',
    };
  }
  if (pathname !== LLM_GATEWAY_PATHS.list && pathname !== `${LLM_GATEWAY_PATHS.list}/`) {
    return {
      title: 'Gateway Connection',
      subtitle: 'Copy connection details and wire this gateway into an application.',
    };
  }
  return {
    title: 'LLM Gateway',
    subtitle: 'Create and manage governed LLM endpoints for chat, embeddings, and custom serving protocols.',
  };
}

function GatewayBody() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { title, subtitle } = titleForPath(location.pathname);
  const toast = (type: 'success' | 'error' | 'info', message: string) => showToast({ type, message });
  const isList =
    location.pathname === LLM_GATEWAY_PATHS.list || location.pathname === `${LLM_GATEWAY_PATHS.list}/`;
  const { ready, loading, loadError, reload } = useLlmGateway();

  return (
    <div className={`w-full min-w-0 -m-3 min-h-[calc(100vh-1.5rem)] ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
      <header className={`px-6 pt-6 ${isLight ? 'bg-slate-50' : 'bg-neutral-950'}`}>
        {!isList ? (
          <button type="button" className={`${ghostBtn(isLight)} mb-3 h-8 px-2.5 text-xs`} onClick={() => navigate(LLM_GATEWAY_PATHS.list)}>
            <ChevronLeft size={14} />
            Back to gateways
          </button>
        ) : null}
        <p className={`m-0 text-[11px] font-semibold uppercase tracking-[0.08em] ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`}>
          {FABRIC_HEADER_EYEBROW}
        </p>
        <h1 className={`mt-1 text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-neutral-50'}`}>{title}</h1>
        <p className={`mt-1.5 max-w-2xl text-[13px] ${isLight ? 'text-slate-500' : 'text-neutral-400'}`}>{subtitle}</p>
      </header>
      <div className="px-6 py-5">
        {!ready && loading ? (
          <div className={`flex items-center justify-center py-24 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            <Loader size={16} className="animate-spin mr-2" />
            Loading LLM Gateway…
          </div>
        ) : !ready && loadError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className={`text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>Could not load LLM Gateway</p>
            <p className={`text-sm mt-1 max-w-lg ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{loadError}</p>
            <button type="button" className={`${primaryBtn()} mt-4`} onClick={() => void reload()}>
              Retry
            </button>
          </div>
        ) : (
          <Outlet context={{ isLight, onToast: toast } satisfies LlmGatewayOutletContext} />
        )}
      </div>
    </div>
  );
}

export function useLlmGatewayPage(): LlmGatewayOutletContext {
  return useOutletContext<LlmGatewayOutletContext>();
}

export default function LlmGatewayScreen() {
  return (
    <LlmGatewayProvider>
      <GatewayBody />
    </LlmGatewayProvider>
  );
}
